/**
 * Beacon Golf PWA — entry point (BEACON-34)
 *
 * Phase 0: app shell + service worker registration
 * Phase 1A (this commit): TileManager + MapApp instantiation, Mapbox satellite live
 * Phase 1B: Google Maps Tiles primary with Mapbox fallback
 * Phase 2: course-brief panel renders inside #brief-panel
 * Phase 3: GPS overlay + point-in-polygon hole detection
 */

import { TileManager } from "./tiles/TileManager.js";
import { MapApp, loadCourseLocation } from "./map/MapApp.js";
import { BriefLoader } from "./brief/BriefLoader.js";
import { BriefNav } from "./brief/BriefNav.js";
import { ScenarioPicker } from "./brief/ScenarioPicker.js";
import { HolePicker } from "./brief/HolePicker.js";
import { CoursePicker } from "./brief/CoursePicker.js";
import { updateHeadForCourse, resetHeadToDefault } from "./util/head-meta.js";
import { recordLastViewed } from "./util/last-viewed.js";

// BEACON-61 (UMAMI-53 mirror): __APP_VERSION__ is injected at build time by
// vite from `scripts/build-env.mjs#resolveAppVersion()`. Format is
// `<ISO-week>.<short-sha>` (e.g. `2026-W18.f77b4fb`). See
// `docs/version-discipline.md` for rationale.
// eslint-disable-next-line no-undef
const BUILD_ID = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : (import.meta.env.VITE_BUILD_ID || "dev");
const DEFAULT_COURSE_SLUG = import.meta.env.VITE_DEFAULT_COURSE_SLUG || "crandon-golf";

// Resolve the active course slug from the URL (?course=<slug>) with a sane
// fallback to the build-time default. Whitelisted to lowercase kebab-case so
// we don't fetch arbitrary paths if someone fuzzes the param.
const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;
function resolveCourseSlug() {
  const params = new URLSearchParams(window.location.search);
  const raw = (params.get("course") || "").trim();
  if (raw && SLUG_RE.test(raw)) return { slug: raw, fallback: false, requested: raw };
  if (raw) return { slug: DEFAULT_COURSE_SLUG, fallback: true, requested: raw };
  return { slug: DEFAULT_COURSE_SLUG, fallback: false, requested: null };
}
const { slug: COURSE_SLUG, fallback: SLUG_FELL_BACK, requested: SLUG_REQUESTED } = resolveCourseSlug();
const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || "";

// BEACON-52 (Round Mode, audit §6 Friday-shippable):
// `?mode=round` collapses the chrome to brief-only with a bottom-pinned
// hole-rail. Map is hidden, course-picker is locked, header reduces to title.
// Driven entirely by `body[data-mode="round"]` CSS hooks; exit-pill click
// strips `mode=round` from URL and reloads (preserves `?course=`).
const ROUND_MODE = (() => {
  const m = (new URLSearchParams(window.location.search).get("mode") || "").trim().toLowerCase();
  return m === "round";
})();
if (ROUND_MODE) {
  document.body.dataset.mode = "round";
}

// Render build id (audit-friendly footer)
const buildEl = document.querySelector("[data-build-id]");
if (buildEl) buildEl.textContent = `build ${BUILD_ID}`;

// BEACON-52: wire the exit-round-mode pill (always present in DOM, CSS-revealed in round mode).
function wireRoundModeExit() {
  const pill = document.getElementById("round-mode-exit");
  if (!pill) return;
  pill.addEventListener("click", () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("mode");
    // BEACON-50 W0 R1-fold-4 (CER-30 ER-W0-4): location.replace not assign
    // — .assign creates a NEW history entry, so back-button from default-mode
    // returns to round mode (cycle, not escape; cart-screen UC3 user mid-round
    // is the most likely to mash back-button). .replace replaces the entry
    // so back exits the app, matching exit-pill semantics.
    // ER suggested no-reload `history.replaceState` but that leaves map
    // bootstrap skipped from round-mode boot — .replace re-runs full boot
    // with the map this time, no half-state.
    window.location.replace(url.toString());
  });
}
wireRoundModeExit();

// Service worker registration (production only — dev uses Vite HMR)
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => console.info("[beacon-golf] sw registered:", reg.scope))
      .catch((err) => console.warn("[beacon-golf] sw registration failed:", err));
  });
}

// Connectivity tracking on body (CSS hooks)
function updateConnectivity() {
  document.body.dataset.connectivity = navigator.onLine ? "online" : "offline";
}
window.addEventListener("online", updateConnectivity);
window.addEventListener("offline", updateConnectivity);
updateConnectivity();

/* BEACON-50 v0.7 (UX-F4): non-blocking toast for slug-fallback + status. */
function showToast(text, { ttlMs = 5000 } = {}) {
  const el = document.getElementById("beacon-toast");
  if (!el) return;
  el.textContent = text;
  el.hidden = false;
  // Tap to dismiss.
  const dismiss = () => { el.hidden = true; el.removeEventListener("click", dismiss); };
  el.addEventListener("click", dismiss);
  if (ttlMs) setTimeout(dismiss, ttlMs);
}

/* BEACON-50 v0.7 (ER-A11Y-2): polite live-region announcements. */
function announce(text) {
  const el = document.getElementById("brief-announcer");
  if (!el) return;
  // Clear-then-set forces SR re-announcement even if text is unchanged.
  el.textContent = "";
  setTimeout(() => { el.textContent = text; }, 50);
}

/* BEACON-50 v0.7 (BC #4 + ER-A11Y-1): editorial chrome strings. */
const chromeCopy = {
  loadingMap: "Loading map…",
  noToken: "Map needs configuration. Brief is still usable below.",
  mapOffline: "Map unavailable — check connection, then refresh.",
  loadingBrief: (course) => course ? `Loading ${course}…` : "Loading brief…",
  briefLoaded: (course) => course ? `${course} brief loaded.` : "Brief loaded.",
  switchingCourse: (course) => `Switching to ${course}…`,
  slugInvalidToast: (req, fallback) =>
    `We couldn’t find “${req}” — showing ${fallback} instead.`,
};

// Boot the map stack
async function boot() {
  const statusEl = document.getElementById("tile-status");
  const mapContainer = document.getElementById("map");

  if (!mapContainer) {
    console.error("[beacon-golf] #map container missing — bailing");
    return;
  }

  // BEACON-55 R2 (CDR-94 F-06 + ER-W2-1): resolve the course + write head
  // metadata + last-viewed pointer BEFORE the MAPBOX_TOKEN early-return AND
  // before tile negotiation. Two folds compose:
  //   - CDR-94 F-06: token outage previously bailed boot() with the tab
  //     title still reading "Beacon Golf". Share previews + PWA shortcuts
  //     degraded silently to static index.html OG meta. Now head meta runs
  //     regardless of map config; the map can still fail loud below.
  //   - ER-W2-1: tile negotiation is the slow leg of boot (Google probe +
  //     Mapbox health check, 500-1500ms cold). Title flips per-course in
  //     first frame instead of after negotiation.
  // Hoisted above the MAPBOX_TOKEN gate AND above ROUND_MODE branching so
  // every code path benefits.
  //
  // BEACON-50 W0 R1-fold-2 (CDR-90 P1 F-2/F-3/F-6): also hoists per-course
  // HOLES wiring (--round-mode-cols + body[data-course-holes]) for non-18
  // courses (Shorty's 19, Bandon Preserve 13).
  //
  // Note: loadCourseLocation() and the head metadata fetch hit
  // /courses/<slug>/location.json from the same SW-cached source; the second
  // fetch is served from cache (force-cache) and adds no network cost.
  const initialView = await loadCourseLocation(COURSE_SLUG);

  const HOLES = Number(initialView?.holes) || 18;
  document.documentElement.style.setProperty("--round-mode-cols", String(HOLES));
  document.body.dataset.courseHoles = String(HOLES);
  console.info(
    `[beacon-golf] course location: ${initialView.name} center=${initialView.center.join(",")} zoom=${initialView.zoom}`
  );

  // BEACON-55 D.1 / D.2: per-course <title> + og:* + IDB last-viewed pointer.
  try {
    const headRes = await fetch(`/courses/${COURSE_SLUG}/location.json`, { cache: "force-cache" });
    const headCourse = headRes.ok ? await headRes.json() : { name: initialView?.name || COURSE_SLUG, slug: COURSE_SLUG };
    updateHeadForCourse(headCourse, COURSE_SLUG);
    recordLastViewed(COURSE_SLUG, headCourse.name || initialView?.name || COURSE_SLUG);
  } catch (err) {
    // CCQ-430 R1 L-4 fold: on head-meta fetch failure, reset to default OG
    // rather than ship `og:image=/og/<slug>.jpg` for a slug whose card may
    // not exist post-future-migration. The trigger today is rare (the
    // previous loadCourseLocation succeeded with the same JSON), but the
    // catch arm should fail safely. recordLastViewed still runs because we
    // know the course exists — only the *meta* fetch failed.
    console.warn("[beacon-golf] head metadata fetch failed:", err);
    resetHeadToDefault();
    recordLastViewed(COURSE_SLUG, initialView?.name || COURSE_SLUG);
  }

  // BEACON-50 W0 R1-fold-3 (CCQ-422 H-2): MAPBOX_TOKEN only required when
  // the map will actually render. Round mode hides the map entirely and is
  // designed to skip the entire map-bootstrap path (TileManager negotiation,
  // Mapbox SDK init, tile prefetches, WebGL context). Don't early-bail in
  // round mode just because the token isn't present. Head meta has already
  // been written above, so token outage doesn't poison share previews.
  if (!MAPBOX_TOKEN && !ROUND_MODE) {
    statusEl.textContent = chromeCopy.noToken;
    statusEl.dataset.source = "offline";
    console.error("[beacon-golf] VITE_MAPBOX_ACCESS_TOKEN missing");
    return;
  }

  // BEACON-50 W0 R1-fold-3: gate ALL map bootstrap on !ROUND_MODE so the
  // Mapbox SDK isn't instantiated, TileManager doesn't negotiate, tiles
  // aren't prefetched, and WebGL context isn't held on the cart-screen UC3
  // hot path (battery, bandwidth, TTI).
  let mapApp = null;
  let tileManager = null;
  if (!ROUND_MODE) {
    // Dev override: ?force=mapbox or ?force=google pins the source for screenshot/A-B testing.
    const params = new URLSearchParams(location.search);
    const forceSource = params.get("force");

    tileManager = new TileManager({
      googleApiKey: GOOGLE_API_KEY || null,
      mapboxToken: MAPBOX_TOKEN,
      statusEl,
      forceSource,
    });
    await tileManager.negotiate();

    mapApp = new MapApp({
      container: mapContainer,
      tileManager,
      mapboxToken: MAPBOX_TOKEN,
      initialView,
    });
    // Pre-populate the cache so centerOnCourse() doesn't re-fetch
    mapApp.courseViews.set(COURSE_SLUG, initialView);

    try {
      await mapApp.init();
      console.info(
        `[beacon-golf] map ready — source=${tileManager.activeSource} course=${COURSE_SLUG} build=${BUILD_ID}`
      );
    } catch (err) {
      console.error("[beacon-golf] map init failed:", err);
      // BEACON-50 ER-COMP-1: user-facing copy must read like a path forward,
      // not a dev log. Original: "Map init failed — check console".
      statusEl.textContent = chromeCopy.mapOffline;
      statusEl.dataset.source = "offline";
    }
  } else {
    console.info(
      `[beacon-golf] round mode active — skipping map/tile bootstrap (course=${COURSE_SLUG} build=${BUILD_ID})`
    );
  }

  // Boot the course-brief panel in parallel with the map (Phase 2)
  const briefPanel = document.getElementById("brief-panel");
  let briefLoader = null;
  let scenarioPicker = null;
  // Capture course display name from initialView so editorial chrome strings
  // can name the course (BC #4 "Loading <Course>…").
  const courseDisplayName = initialView?.name || COURSE_SLUG;
  // Update placeholder copy with course name immediately (BC #4).
  const placeholder = briefPanel?.querySelector(".brief-placeholder");
  if (placeholder) placeholder.textContent = chromeCopy.loadingBrief(courseDisplayName);
  if (briefPanel) {
    briefLoader = new BriefLoader({
      panel: briefPanel,
      briefUrl: `/briefs/${COURSE_SLUG}/latest.html`,
      // BEACON-50 v0.7: BriefLoader uses chromeCopy for error strings.
      copy: chromeCopy,
      courseDisplayName,
      // BEACON-53 (audit fix): per-course hole bound from location.json.
      // Shorty's=19, Bandon Preserve=13, regulation=18. Defaults to 18.
      holeCount: initialView?.holes ?? 18,
    });
    try {
      const meta = await briefLoader.load();
      console.info(`[beacon-golf] brief loaded — date=${meta.date} tee=${meta.tee}`);
      updateBriefMeta(meta);
      briefPanel.setAttribute("aria-busy", "false");
      // ER-A11Y-2: announce brief load completion to AT.
      announce(chromeCopy.briefLoaded(courseDisplayName));
      // BEACON-78: render the sticky pill-nav for STRATEGY / WIND-MATRIX /
      // R1 / R2 / HOLES / CARD / T-0 sections. Compact mode in round-mode.
      // Auto-hides if <2 sections detected (e.g. crandon stub).
      try {
        const briefNav = new BriefNav({
          panel: briefPanel,
          compact: ROUND_MODE,
        });
        briefNav.render();
        // Re-render after scenario swaps (ScenarioPicker calls briefLoader.loadUrl()
        // which replaces panel innerHTML, removing the nav).
        briefPanel.addEventListener("brief:loaded", () => briefNav.render());
      } catch (navErr) {
        // Nav is non-critical chrome — never block the brief on it.
        console.warn("[beacon-golf] brief-nav render skipped:", navErr);
      }
    } catch (err) {
      console.warn("[beacon-golf] brief load failed (panel will show error):", err);
      briefPanel.setAttribute("aria-busy", "false");
      announce("Brief failed to load. Use the retry button or switch course.");
    }

    // BEACON-50 v0.7 (UX-F4): non-blocking toast on slug fallback.
    if (SLUG_FELL_BACK && SLUG_REQUESTED) {
      showToast(chromeCopy.slugInvalidToast(SLUG_REQUESTED, courseDisplayName));
    }

    // Phase 2.5: scenario picker (dev/testing UX)
    const toolbar = document.getElementById("brief-toolbar");
    const select = document.getElementById("scenario-select");
    if (toolbar && select) {
      scenarioPicker = new ScenarioPicker({
        toolbar,
        select,
        courseSlug: COURSE_SLUG,
        onChange: async (url) => {
          briefPanel.setAttribute("aria-busy", "true");
          announce("Loading conditions…");
          const meta = await briefLoader.loadUrl(url);
          updateBriefMeta(meta);
          briefPanel.setAttribute("aria-busy", "false");
          announce(chromeCopy.briefLoaded(courseDisplayName));
          // BEACON-50 P1 (UX): respect prefers-reduced-motion.
          const prm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
          briefPanel.scrollTo({ top: 0, behavior: prm ? "auto" : "smooth" });
          return meta;
        },
      });
      // Fire-and-forget; picker hides itself if manifest unavailable
      scenarioPicker.init();
    }
  }

  // Phase A: hole picker (manual current-hole selector — GPS in v0.5)
  let holePicker = null;
  const holeSlot = document.getElementById("hole-picker-slot");
  if (briefLoader && holeSlot) {
    // BEACON-50 v0.7 (ER-COMP-5): pass courseSlug so localStorage cache
    // doesn't bleed selected-hole state across courses.
    // BEACON-53 (audit fix): pass holeCount so picker renders per-course
    // (Shorty's shows 19 buttons, Bandon Preserve shows 13, others 18).
    holePicker = new HolePicker({
      container: holeSlot,
      briefLoader,
      courseSlug: COURSE_SLUG,
      holeCount: initialView?.holes ?? 18,
    });
    // BriefLoader fires brief:loaded after each load(); HolePicker listens.
    // For the FIRST load (which already happened above), re-dispatch so the
    // picker can populate its presentHoles set on initial boot.
    if (briefLoader.presentHoles) {
      briefPanel.dispatchEvent(
        new CustomEvent("brief:loaded", {
          bubbles: true,
          detail: { presentHoles: briefLoader.presentHoles },
        })
      );
    }
  }

  // Course picker (header dropdown) — populates from /courses/index.json
  let coursePicker = null;
  const coursePickerSlot = document.getElementById("course-picker-slot");
  if (coursePickerSlot) {
    coursePicker = new CoursePicker({
      container: coursePickerSlot,
      activeSlug: COURSE_SLUG,
    });
    // Fire-and-forget: picker hides itself if index.json is unavailable
    coursePicker.init().catch((err) => console.warn("[course-picker] init failed:", err));
  }

  // Expose for ad-hoc DevTools work
  window.__beaconGolf = {
    build: BUILD_ID,
    course: COURSE_SLUG,
    tileManager,
    mapApp,
    briefLoader,
    scenarioPicker,
    holePicker,
    coursePicker,
  };
}

/** Update the small caption in the brief toolbar.
   BC #5: include weather summary when present so the meta tells you what
   you'll need on the course, not just the calendar coordinates. */
function updateBriefMeta(meta) {
  const el = document.getElementById("brief-meta");
  if (!el || !meta) return;
  const parts = [];
  if (meta.date) parts.push(meta.date);
  if (meta.tee) parts.push(`${meta.tee} tees`);
  if (meta.conditions) parts.push(meta.conditions);
  el.textContent = parts.join(" · ");
}

boot();
